import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import useEditorStore from './useEditorStore';
import useFinalizationDataStore from './useFinalizationDataStore';
import { EASING_FUNCTION } from '../utils/EasingFunctions';

const useRectificationStore = create(
  devtools((set, get) => ({
    // Measure Reduction Tool
    measureReduction: {
      measurements: [], // { id, scan { circumference, medioLat, antPost }, rectified { circumference, medioLat, antPost }, percentage, falloff, height }
      currentIndex: 0
    },

    // Scoop/Recharge Tool
    scoopRecharge: {
      enabled: true,
      brushRadius: 1.0,
      paintingMode: false,
      selectedZoneId: 0,
      zones: [],  // { id, name, summitValue, color, points, bezierHandles, influences }
      previewVisible: true,
      previewMesh: null,
      curveResolution: 100, // Default curve sampling resolution
      highAccuracyResolution: 200, // High accuracy resolution for final operations
      showControlsForAllZones: false, // Show curve controls for all zones or just selected
      easing: EASING_FUNCTION.EASE_OUT_CUBIC, // Easing function for gradient deformation
    },

    // Blocking Zone Tool
    blockingZone: {
      enabled: true,
      radius: 1.0,
      mode: 'paint', // paint, erase
      mask: null,
    },

    // Global Reduction Tool
    globalReduction: {
      enabled: true,
      value: 0,
      valueType: '%',
      unit: 'mm',
      previewVisible: true,
      previewMesh: null,
    },

    // Smoothning Tool
    smoothning: {
      enabled: true,
      zones: [],  // { id, name, smootheningValue, color, isExpanded, influences }
      brushRadius: 0.75,
      previewVisible: true,
      previewMesh: null,
    },

    // Actions
    setMeasureReduction: (data) => {
      set({ measureReduction: { ...get().measureReduction, ...data } })
    },
    setScoopRecharge: (data) => set({ scoopRecharge: { ...get().scoopRecharge, ...data } }),
    setBlockingZone: (data) => set({ blockingZone: { ...get().blockingZone, ...data } }),
    setGlobalReduction: (data) => set({ globalReduction: { ...get().globalReduction, ...data } }),
    setSmoothning: (data) => set({ smoothning: { ...get().smoothning, ...data } }),
    
    //#region Global Reduction Apply/Undo
    isGlobalReductionApplied: false,
    setIsGlobalReductionApplied: (status) => set({ isGlobalReductionApplied: status }),
    isGlobalReductionAppliedFromDraft: false,
    setIsGlobalReductionAppliedFromDraft: (status) => set({ isGlobalReductionAppliedFromDraft: status }),

    currentGlobalReductionCommand: null,
    setCurrentGlobalReductionCommand: (command) => set({ currentGlobalReductionCommand: command }),

    createGlobalReductionCommand: () => {
      const { globalReduction } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const preview = globalReduction.previewMesh;
      if (!preview) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();

      // Clone geometry and matrix from preview
      const cloneGeom = new THREE.BufferGeometry().copy(preview.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(preview.matrix);

      return {
        execute: () => {
          // Remove preview
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
          }

          const newMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          newMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          replaceShadedMesh(newMesh);

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationGlobalReduction(
            globalReduction.value,
            globalReduction.valueType,
            globalReduction.unit
          );

          get().setIsGlobalReductionApplied(true);
          get().setGlobalReduction({ previewVisible: false, previewMesh: null });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsGlobalReductionApplied(false);
        }
      };
    },
    //#endregion

    //#region Rectification Measure 
    rectifiedMeshRef: { current: null }, // Reference to the rectified mesh

    //rectify measure preview
    rectifiedPreview: { enabled: true },
    // add setters
    setRectifiedPreview: (enabled) => set(state => ({
      rectifiedPreview: { ...state.rectifiedPreview, enabled }
    })),

    isRectificationApplied : false,
    setIsRectificationApplied: (status) => set({ isRectificationApplied: status }),
    isRectificationAppliedFromDraft: false,
    setIsRectificationAppliedFromDraft: (status) => set({ isRectificationAppliedFromDraft: status }),

    currentRectificationCommand: null,
    setCurrentRectificationCommand: (command) => set({ currentRectificationCommand: command }),

    createRectificationCommand: () => {
      const { rectifiedMeshRef, measureReduction } = get();

      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      if (!rectifiedMeshRef.current) return null; // Return null if command can't be created

      // Capture the original mesh state for undo
      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();
      const originalReduction = JSON.parse(JSON.stringify(measureReduction));

      // Store a copy of the rectified mesh for redo operations
      const cloneGeom = new THREE.BufferGeometry().copy(rectifiedMeshRef.current.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(rectifiedMeshRef.current.matrix);

      return {
        execute: () => {
          // **CRITICAL FIX**: Ensure geometry has proper spatial structures before applying
          cloneGeom.computeVertexNormals();
          cloneGeom.computeBoundingBox();
          cloneGeom.computeBoundingSphere();
          // Dispose any stale BVH tree (will be rebuilt by tools that need it)
          if (cloneGeom.boundsTree) {
            cloneGeom.disposeBoundsTree();
          }
          
          const rectifiedMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          rectifiedMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          rectifiedMesh.matrix.decompose(rectifiedMesh.position, rectifiedMesh.quaternion, rectifiedMesh.scale);
          rectifiedMesh.matrixAutoUpdate = true;
          replaceShadedMesh(rectifiedMesh);

          if(rectifiedMeshRef.current){
            rectifiedMeshRef.current.parent?.remove(rectifiedMeshRef.current);
            // **CRITICAL FIX**: Dispose BVH tree before disposing geometry
            if (rectifiedMeshRef.current.geometry?.boundsTree) {
              rectifiedMeshRef.current.geometry.disposeBoundsTree();
            }
            rectifiedMeshRef.current.geometry.dispose();
            rectifiedMeshRef.current.material.dispose();
          }
          rectifiedMeshRef.current = null;

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationMeasureReduction(
            measureReduction.measurements
          );

          get().setIsRectificationApplied(true);
          get().setRectifiedPreview(false);
          get().setMeasureReduction({ measurements: [], currentIndex: 0 });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsRectificationApplied(false);
          get().setMeasureReduction({ measurements: originalReduction.measurements, currentIndex: originalReduction.currentIndex });
        }
      };
    },
    //#endregion

    //#region Scoop/Recharge Tool
    isScoopRechargeApplied: false,
    setIsScoopRechargeApplied: (status) => set({ isScoopRechargeApplied: status }),
    isScoopRechargeAppliedFromDraft: false,
    setIsScoopRechargeAppliedFromDraft: (status) => set({ isScoopRechargeAppliedFromDraft: status }),

    currentScoopRechargeCommand: null,
    setCurrentScoopRechargeCommand: (command) => set({ currentScoopRechargeCommand: command }),

    /* Scoop/Recharge Tool */
    createScoopRechargeCommand: () => {
      const { scoopRecharge } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      if(scoopRecharge.zones.length === 0 || !scoopRecharge.previewMesh) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();
      // Deep clone the scoopRecharge state including zones with their influences
      // Special handling for Float32Array influences that JSON.stringify can't handle properly
      const originalScoopRecharge = {
        ...scoopRecharge,
        zones: scoopRecharge.zones.map(zone => ({
          ...zone,
          influences: zone.influences ? new Float32Array(zone.influences) : zone.influences
        }))
      };

      const cloneGeom = new THREE.BufferGeometry().copy(scoopRecharge.previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(scoopRecharge.previewMesh.matrix);

      return {
        execute: () => {
          // Remove and dispose any lingering preview mesh before replacing shaded mesh
          const latestSR = get().scoopRecharge;
          const preview = latestSR?.previewMesh;
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
            preview.userData?.baseGeometry?.dispose?.();
          }

          const scoopRechargeMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          scoopRechargeMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          scoopRechargeMesh.matrix.decompose(scoopRechargeMesh.position, scoopRechargeMesh.quaternion, scoopRechargeMesh.scale);
          scoopRechargeMesh.matrixAutoUpdate = true;
          replaceShadedMesh(scoopRechargeMesh);

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationScoopRecharge(
            originalScoopRecharge.zones
          );

          get().setIsScoopRechargeApplied(true);
          get().setScoopRecharge({
            zones: [],
            previewVisible: false,
            previewMesh: null
          });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsScoopRechargeApplied(false);
          // Restore the complete original state including zones with their influences
          // Note: previewMesh is set to null instead of originalScoopRecharge.previewMesh
          // because the preview was disposed during execute(). Setting it to null
          // forces ensurePreview() to recreate it from the restored shadedMesh.
          get().setScoopRecharge({
            zones: originalScoopRecharge.zones,
            previewVisible: originalScoopRecharge.previewVisible,
            previewMesh: null, // Force recreation instead of restoring disposed reference
            brushRadius: originalScoopRecharge.brushRadius,
            selectedZoneId: originalScoopRecharge.selectedZoneId
          });
        }
      };
    },
    //#endregion

    //#region Smoothning Tool
    isSmootheningApplied: false,
    setIsSmootheningApplied: (status) => set({ isSmootheningApplied: status }),
    isSmootheningAppliedFromDraft: false,
    setIsSmootheningAppliedFromDraft: (status) => set({ isSmootheningAppliedFromDraft: status }),

    currentSmootheningCommand: null,
    setCurrentSmootheningCommand: (command) => set({ currentSmootheningCommand: command }),
    
    createSmootheningCommand: () => {
      const { smoothning } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      if(smoothning.zones.length === 0 || !smoothning.previewMesh) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMaterial = sceneRefs.material;
      const originalMatrix = originalMesh.matrix.clone();

      // Deep clone the smoothning state including zones with their influences
      const originalSmoothning = {
        ...smoothning,
        zones: (smoothning.zones || []).map(zone => ({
          ...zone,
          influences: zone.influences ? new Float32Array(zone.influences) : zone.influences
        }))
      };

      // Clone the preview geometry and transform
      const cloneGeom = new THREE.BufferGeometry().copy(smoothning.previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(smoothning.previewMesh.matrix);

      return {
        execute: () => {
          // Remove and dispose any lingering preview mesh before replacing shaded mesh
          const latest = get().smoothning;
          const preview = latest?.previewMesh;
          if (preview && preview.isMesh) {
            preview.parent?.remove(preview);
            preview.geometry?.dispose?.();
            preview.material?.dispose?.();
            preview.userData?.baseGeometry?.dispose?.();
          }

          const smoothedMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          smoothedMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          smoothedMesh.matrix.decompose(smoothedMesh.position, smoothedMesh.quaternion, smoothedMesh.scale);
          smoothedMesh.matrixAutoUpdate = true;
          replaceShadedMesh(smoothedMesh);

          // Save to finalization store
          useFinalizationDataStore.getState().saveRectificationSmoothening(
            originalSmoothning.zones
          );

          get().setIsSmootheningApplied(true);
          get().setSmoothning({
            zones: [],
            previewVisible: false,
            previewMesh: null
          });
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          get().setIsSmootheningApplied(false);
          // Restore the complete original state including zones with their influences
          // Note: previewMesh is set to null instead of originalSmoothning.previewMesh
          // because the preview was disposed during execute(). Setting it to null
          // forces ensurePreview() to recreate it from the restored shadedMesh.
          get().setSmoothning({
            zones: originalSmoothning.zones,
            previewVisible: originalSmoothning.previewVisible,
            previewMesh: null, // Force recreation instead of restoring disposed reference
            brushRadius: originalSmoothning.brushRadius
          });
        }
      };
    },
    //#endregion

    // Reset all data
    reset: () => set({
      measureReduction: {
        measurements: [],
        currentIndex: 0
      },
      scoopRecharge: { enabled: true, brushRadius: 1.0, paintingMode: false, selectedZoneId: 0, zones: [], easing: EASING_FUNCTION.EASE_OUT_SINE },
      blockingZone: { enabled: true, radius: 5.0, position: { x: 0, y: 0, z: 0 } },
      globalReduction: { enabled: true, value: 0, valueType: '%', unit: 'mm', previewVisible: true, previewMesh: null },
      smoothning: { enabled: true, zones: [], brushRadius: 1.0 }
    })
  }))
);

export default useRectificationStore;